// Import components
import React, { useMemo, useState } from "react";
import Breadcrumbs from '../../../Components/Common/Breadcrumb';
import TableContainer from '../../../Components/Common/TableContainer';
import { Modal, Label, ModalHeader, Card, CardBody, Row, Col, Alert, CardHeader, Badge, Button, Nav, NavItem, TabContent, TabPane, NavLink } from 'reactstrap';
import { Link } from "react-router-dom";
import axios from "axios";
import { useEffect } from "react";
import defaultUserImg from "../../../assets/images/defaultuserimgdark.png";
import classnames from "classnames";
import PETaskManagement from "./PETaskManagement";
import QuickBooksLogin from "../others/Quickbooks/QuickBooksLogin";
import PartnerCommunicationPreferences from "../../../Components/Common/PartnerCommunicationPreferences";

interface column {
    header: string;
    accessorKey?: string;
    accessorFn?: (row: any) => string;
    enableColumnFilter: boolean;
    enableSorting: boolean;
    cell?: (info: any) => React.ReactNode;
}

const PEAssignmentManagement = () => {
    const [activeTab, toggleTab] = useState<any>("2");
    const assignmentId = new URLSearchParams(window.location.search).get("assignment_id");

    const [showloader, setshowloader] = useState(true);
    const [alertMsgg, setAlertMsgg] = useState<any>({ open: false, type: "success", content: "Messages here.", time: "2500" });

    const [assignmentData, setAssignmentData] = useState<Record<string, any>>({});
    const [clientData, setClientData] = useState<Record<string, any>>({});
    const [tasksData, setTasksData] = useState<any[]>([]);
    const [productsData, setProductsData] = useState<any[]>([]);

    // Get user role
    const userRole = localStorage.getItem('userole_');
    const isClientOrAdmin = userRole === 'cst' || userRole === 'adm';

    // Communication preferences
    const [commPreferences, setCommPreferences] = useState<any>(null);
    const [loadingCommPrefs, setLoadingCommPrefs] = useState(false);

    const handleAlertMsggClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === "clickaway") { return; }
        setAlertMsgg({ ...alertMsgg, open: false });
    };

    const fetch_tasks = async () => {
        try {
            setshowloader(true);
            const response = await axios.get(
                `/assignments/listTasks`,
                {
                    params: { assignment: assignmentId }
                },
                { withCredentials: true }
            );
            if (response && response.status === "SENT") {
                setTasksData(response.tasks || []);
                setAssignmentData(response.assignment || {});
                setClientData(response.assignment.client || {});
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    const fetch_products = async () => {
        try {
            setshowloader(true);
            const response = await axios.get(
                `/assignments/getAssignmentProducts`,
                {
                    params: { assignment: assignmentId }
                },
                { withCredentials: true }
            );
            if (response && response.status === "SENT") {
                setProductsData(response.products || []);
            }
            setshowloader(false);
        } catch (error: any) {
            if (error.response) {
                if ([401, 422].includes(error.response.status)) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: error.response.data?.error || "Error occurred", type: "error" });
                } else if (error.response.status === 403) {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "Session expired!", type: "error" });
                    setTimeout(() => { window.location.href = "/logout"; }, 2000);
                } else {
                    setAlertMsgg({ ...alertMsgg, open: true, content: "An error occurred", type: "error" });
                }
            } else {
                setAlertMsgg({ ...alertMsgg, open: true, content: "Network error", type: "error" });
            }
            setshowloader(false);
        }
    };

    useEffect(() => {
        fetch_tasks();
    }, []);

    // Helper function to convert text to title case
    const toTitleCase = (str: string) => {
        if (!str) return '';
        return str.toLowerCase().split(' ').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
    };

    // Get partner name from assignment
    const getPartnerName = () => {
        if (!assignmentData.assignee) return '';
        if (typeof assignmentData.assignee === 'object' && assignmentData.assignee.name) {
            return toTitleCase(assignmentData.assignee.name || '');
        }
        return '';
    };

    // Get partner ID from assignment
    const getPartnerId = () => {
        if (assignmentData.assignee_type === 'prt' && assignmentData.assignee) {
            if (typeof assignmentData.assignee === 'object' && assignmentData.assignee.id) {
                return String(assignmentData.assignee.id);
            }
        }
        return null;
    };

    // Fetch communication preferences (after purchase)
    const fetchCommPreferences = async (partnerId: string) => {
        if (!partnerId) return;
        try {
            setLoadingCommPrefs(true);
            const response = await axios.get(
                '/payments/getPartnerCommunicationPreferences',
                {
                    params: { partner_id: partnerId, mode: 'after' },
                    withCredentials: true
                }
            );
            if (response && response.communication_preferences) {
                setCommPreferences(response.communication_preferences);
            } else {
                setCommPreferences(null);
            }
        } catch (error: any) {
            console.error('Error fetching communication preferences:', error);
            setCommPreferences(null);
        } finally {
            setLoadingCommPrefs(false);
        }
    };

    // Fetch communication preferences when Sales support tab is clicked
    useEffect(() => {
        if (activeTab === '5' && isClientOrAdmin) {
            const partnerId = getPartnerId();
            if (partnerId) {
                fetchCommPreferences(partnerId);
            }
        }
    }, [activeTab, assignmentData]);

    document.title = "Assignment | BizServe";


    return (
        <div className="page-content">
            <div className="container-fluid">
                <Breadcrumbs title="Assignment" breadcrumbItem="Details" />
                {alertMsgg.open && (
                    <Alert
                        color={alertMsgg.type === "success" ? "success" : "danger"}
                        toggle={handleAlertMsggClose}
                        fade={false}
                        style={{ fontWeight: "bold" }}
                    >
                        {alertMsgg.content}
                    </Alert>
                )}
                <div className="loaderContainer" hidden={!showloader}>
                    <div className="spinner">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                </div>
                <Col xl={12}>
                    <Card className="shadow-none">
                        <Nav tabs
                            className="nav-tabs-custom justify-content-center pt-1 nav-justified"
                            role="tablist"
                        >
                            <NavItem>
                                <NavLink
                                    to=""
                                    className={classnames({
                                        active: activeTab === "1",
                                    })}
                                    onClick={() => {
                                        toggleTab("1")
                                    }}
                                >
                                    Company Information
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    to=""
                                    className={classnames({
                                        active: activeTab === "2",
                                    })}
                                    onClick={() => {
                                        toggleTab("2")
                                    }}
                                >
                                    <span className="text-capitalize">{assignmentData.domain ? `${assignmentData.domain.name} ` : ""}</span>Tasks
                                </NavLink>
                            </NavItem>
                            <NavItem>
                                <NavLink
                                    to=""
                                    className={classnames({
                                        active: activeTab === "4",
                                    })}
                                    onClick={() => {
                                        toggleTab("4");
                                        if (productsData.length === 0) {
                                            fetch_products();
                                        }
                                    }}
                                >
                                    Orders
                                </NavLink>
                            </NavItem>
                            {(assignmentData.domain && (assignmentData.domain.name.toLowerCase().includes('bookeeping') || assignmentData.domain.name.toLowerCase().includes('bookkeeping'))) && (<NavItem>
                                <NavLink
                                    to=""
                                    className={classnames({
                                        active: activeTab === "3",
                                    })}
                                    onClick={() => {
                                        toggleTab("3")
                                    }}
                                >
                                    Company Financials
                                </NavLink>
                            </NavItem>)}
                            {isClientOrAdmin && assignmentData.assignee_type === 'prt' && (
                                <NavItem>
                                    <NavLink
                                        to=""
                                        className={classnames({
                                            active: activeTab === "5",
                                        })}
                                        onClick={() => {
                                            toggleTab("5");
                                            const partnerId = getPartnerId();
                                            if (partnerId && !commPreferences) {
                                                fetchCommPreferences(partnerId);
                                            }
                                        }}
                                    >
                                        Contact Partner
                                    </NavLink>
                                </NavItem>
                            )}
                        </Nav>

                        <TabContent className="p-4" activeTab={activeTab} style={{ backgroundColor: "#f7f7fa" }}>
                            <TabPane tabId="1">
                                <Col xl={4}>
                                    <Card>
                                        <CardBody className="border-bottom">
                                            <div>
                                                <div className="mb-4 me-3">
                                                    <img
                                                        src={clientData.profile_pic || defaultUserImg}
                                                        alt="Client"
                                                        className="rounded-circle border border-3 border-primary"
                                                        style={{ width: 40, height: 40, objectFit: "cover", boxShadow: "0 4px 16px rgba(0,0,0,0.12)" }}
                                                    />
                                                </div>

                                                <div>
                                                    <h5 className="text-capitalize" >{clientData.name || <span className="text-muted">No Name</span>}</h5>
                                                    <p className="text-muted mb-1">{clientData.email || <span className="text-muted">N/A</span>}</p>
                                                    <p className="text-muted mb-0">{clientData.phone || <span className="text-muted">N/A</span>}</p>
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                </Col>
                            </TabPane>

                            <TabPane tabId="2">
                                <PETaskManagement tasksData={tasksData} assignmentId={assignmentId || ""} onTasksUpdate={fetch_tasks} />
                            </TabPane>

                            <TabPane tabId="3">
                                <QuickBooksLogin />
                            </TabPane>

                            <TabPane tabId="4">
                                <Card>
                                    <CardHeader>
                                        <h5 className="card-title mb-0">Products Assigned to This Assignment</h5>
                                    </CardHeader>
                                    <CardBody>
                                        {productsData.length === 0 ? (
                                            <div className="text-center py-4">
                                                <p className="text-muted">No products assigned to this assignment.</p>
                                            </div>
                                        ) : (
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-hover">
                                                    <thead>
                                                        <tr>
                                                            <th>Title</th>
                                                            <th>Domain</th>
                                                            <th>Purchase Date</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {productsData.map((product: any, index: number) => (
                                                            <tr key={product.order_assignment_id || index}>
                                                                <td>
                                                                    <strong>{product.product_title || 'N/A'}</strong>
                                                                    {product.product_description && (
                                                                        <>
                                                                            <br />
                                                                            <small className="text-muted">{product.product_description}</small>
                                                                        </>
                                                                    )}
                                                                </td>
                                                                <td>{product.domain || 'N/A'}</td>
                                                                <td>
                                                                    {product.order_created_at ? new Date(product.order_created_at).toLocaleDateString() : 'N/A'}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </CardBody>
                                </Card>
                            </TabPane>

                            {/* Contact Partner Tab - Only visible for clients and admins */}
                            {isClientOrAdmin && assignmentData.assignee_type === 'prt' && (
                                <TabPane tabId="5">
                                    <PartnerCommunicationPreferences
                                        partnerId={getPartnerId()}
                                        partnerName={getPartnerName()}
                                        loadingCommPrefs={loadingCommPrefs}
                                        commPreferences={commPreferences}
                                        noPartnerMessage="Partner information not available."
                                        mode="after"
                                    />
                                </TabPane>
                            )}
                        </TabContent>
                    </Card>
                </Col>
            </div>
        </div>
    );
};

export default PEAssignmentManagement;
